import * as THREE from 'three'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { FileInfo } from '../file-info'
import { extractPartName } from '../glb'
import { resolveImportedDisplayName } from '../original-node-name'
import { resolveImportedModelUnitContextFromGltf } from '../imported-model-unit-context'
import { normalizeImportedAssetFileId } from '../../sidecar'
import { asRecord } from '../utils'
import {
  buildNodeId,
  cloneMetadata,
  hasInlineAssemblyLocalGeometry,
  isGeometryObject,
  readNumberValue,
  readStringValue,
  resolveCanonicalAssemblyRoot,
  resolveNodePmi,
  resolveNodeRecords,
  resolveNodeSemanticKind,
  resolvePartReference,
  sanitizePartReference,
  shouldIncludeManifestNode,
  toMatrixTuple,
} from '../assembly-node-utils'
import {
  reparentFlattenedExternalReferenceCarriers,
  resolveNodeSourceEntityId,
  resolveNodeSourceEntityPath,
} from '../assembly-external-reference-fallback'
import type { AssemblyManifest, AssemblyNode } from '../types'

/**
 * 解析外部零部件引用在 manifest 中使用的稳定 key。
 *
 * @param partReference 已规范化的外部引用。
 * @returns 显式文件 UUID；缺失时不把路径推断为外部引用。
 */
function resolveExternalPartReferenceKey(
  partReference: { fileUuid?: string; externalFileId?: string },
): string | undefined {
  const normalizedUuid = partReference.fileUuid?.trim()
  return normalizedUuid || undefined
}

/**
 * 只读分析 GLTF 装配场景。
 *
 * @param _gltf 已解析 GLTF。
 * @param _file GLB 文件描述。
 * @param _version 资产版本。
 * @returns 装配清单、源对象索引与诊断。
 */
export function analyzeGltfAssemblyScene(
  gltf: GLTF,
  file: FileInfo,
  version: string,
): PuppyCAD.GltfAssemblySceneAnalysis {
  const nodesById: Record<string, AssemblyNode> = {}
  const childIdsByNodeId: Record<string, string[]> = {}
  const referencedPartUuids = new Set<string>()
  const externalReferenceFileIdByUuid: Record<string, string> = {}
  const warnings: string[] = []
  const sourceEntityIdByNodeId: Record<string, string> = {}
  const sourceEntityPathByNodeId: Record<string, string> = {}
  const sourceObjectByNodeId = new Map<string, THREE.Object3D>()
  const inlinePartSourceByNodeId = new Map<string, THREE.Object3D>()
  const inlineAssemblySourceByNodeId = new Map<string, THREE.Object3D>()
  const sceneRoot = gltf.scene
  const { canonicalRoot, strippedNodeCount } = resolveCanonicalAssemblyRoot(sceneRoot)
  const rootNodeId = buildNodeId(null, canonicalRoot, 0)

  /**
   * 递归解释一个有业务语义的场景节点。
   *
   * 这里仅构造新的 plain-data 索引；Object3D、matrix、parent 和 userData 均保持原样。
   */
  const visit = (
    object: THREE.Object3D,
    parentNodeId: string | null,
    siblingIndex: number,
  ): void => {
    const records = resolveNodeRecords(object)
    if (!shouldIncludeManifestNode(object, records)) {
      return
    }

    const nodeId = parentNodeId === null && object === canonicalRoot
      ? rootNodeId
      : buildNodeId(parentNodeId, object, siblingIndex)
    const partReference = sanitizePartReference(resolvePartReference(records), file)
    const externalPartReferenceKey = resolveExternalPartReferenceKey(partReference)
    if (externalPartReferenceKey) {
      referencedPartUuids.add(externalPartReferenceKey)
      if (partReference.externalFileId) {
        externalReferenceFileIdByUuid[externalPartReferenceKey] = normalizeImportedAssetFileId(
          partReference.externalFileId,
        )
      }
    }

    const sourceEntityId = resolveNodeSourceEntityId(records)
    const sourceEntityPath = resolveNodeSourceEntityPath(records)
    const semanticKind = resolveNodeSemanticKind(records)
    let inlinePartUuid: string | undefined
    let inlineAssemblyUuid: string | undefined

    if (!partReference.fileUuid && semanticKind !== 'assembly') {
      if (hasInlineAssemblyLocalGeometry(object)) {
        inlinePartUuid = `inline:${file.uuid}:${nodeId}`
        inlinePartSourceByNodeId.set(nodeId, object)
      }
    } else if (
      !partReference.fileUuid
      && semanticKind === 'assembly'
      && hasInlineAssemblyLocalGeometry(object)
    ) {
      inlineAssemblyUuid = `inline-assembly:${file.uuid}:${nodeId}`
      inlineAssemblySourceByNodeId.set(nodeId, object)
    }

    const instanceIndex = readNumberValue(records, [
      'instanceIndex',
      'instanceNo',
      'instanceNumber',
      'instance',
    ])
    const fallbackName = object === canonicalRoot ? extractPartName(file.fileId) : object.type
    nodesById[nodeId] = {
      nodeId,
      parentNodeId,
      name: resolveImportedDisplayName(object, object.name?.trim() || fallbackName),
      semanticKind,
      transform: toMatrixTuple(object.matrix.clone()),
      pmi: resolveNodePmi(records),
      partFileUuid: externalPartReferenceKey ?? inlineAssemblyUuid ?? inlinePartUuid,
      instanceIndex: typeof instanceIndex === 'number'
        ? Math.max(1, Math.round(instanceIndex))
        : undefined,
      configurationName: readStringValue(records, [
        'configurationName',
        'configuration',
        'configName',
        'config',
      ]),
      displayStateName: readStringValue(records, [
        'displayStateName',
        'displayState',
        'displayStatus',
        'display_status',
      ]),
    }
    childIdsByNodeId[nodeId] = []
    sourceObjectByNodeId.set(nodeId, object)
    if (sourceEntityId) {
      sourceEntityIdByNodeId[nodeId] = sourceEntityId
    }
    if (sourceEntityPath) {
      sourceEntityPathByNodeId[nodeId] = sourceEntityPath
    }

    if (!isGeometryObject(object)) {
      let nextChildIndex = 0
      object.children.forEach((child) => {
        const childRecords = resolveNodeRecords(child)
        if (!shouldIncludeManifestNode(child, childRecords)) {
          return
        }
        const childNodeId = buildNodeId(nodeId, child, nextChildIndex)
        childIdsByNodeId[nodeId].push(childNodeId)
        visit(child, nodeId, nextChildIndex)
        nextChildIndex += 1
      })
    }
  }

  visit(canonicalRoot, null, 0)
  reparentFlattenedExternalReferenceCarriers(
    nodesById,
    childIdsByNodeId,
    sourceEntityIdByNodeId,
    sourceEntityPathByNodeId,
  )

  if (strippedNodeCount > 0) {
    warnings.push(
      `装配清单已剥离 ${strippedNodeCount} 层无语义根包装层: ${extractPartName(file.fileId)}`,
    )
  }
  if (Object.keys(nodesById).length === 0) {
    warnings.push('装配清单未解析出任何层级节点')
  }

  const sceneRecord = asRecord(gltf.scene.userData) ?? {}
  const manifest: AssemblyManifest = {
    assemblyFileId: file.fileId,
    assemblyFileUuid: file.uuid,
    version,
    rootNodeId,
    nodesById,
    childIdsByNodeId,
    referencedPartUuids: Array.from(referencedPartUuids),
    externalReferenceFileIdByUuid,
    step: cloneMetadata(sceneRecord.step ?? sceneRecord.stepMetadata ?? sceneRecord.assetStep),
    pmi: cloneMetadata(sceneRecord.pmi),
    unitContext: resolveImportedModelUnitContextFromGltf(gltf),
  }

  return {
    manifest,
    sourceObjectByNodeId,
    inlinePartSourceByNodeId,
    inlineAssemblySourceByNodeId,
    warnings,
  }
}
